import React, { useState, useEffect } from 'react';
import {
	Tabs,
	Input,
	Button,
	Card,
	Alert,
	Typography,
	Tag,
	notification,
	Modal,
	Tooltip,
	Divider,
} from 'antd';
import {
	GlobalOutlined,
	InfoCircleOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	ExclamationCircleOutlined,
	DisconnectOutlined,
	ReloadOutlined,
	LinkOutlined,
	CopyOutlined,
} from '@ant-design/icons';
import { deleteDomain, dnsStatus, getReseller, updateDomain } from '@/api/reseller';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { USER_TYPES } from 'src/enums/EUser';

const { Title, Text, Paragraph } = Typography;

interface DomainStatus {
	domain: string;
	status: 'pending' | 'verified' | 'failed';
	sslStatus: 'pending' | 'active' | 'failed';
	dnsRecord: {
		type: string;
		name: string;
		value: string;
	};
}

const ConnectSubdomainTab = ({
	subdomain,
	setSubdomain,
	domainStatus,
	handleAddDomain,
	handleCheckStatus,
	handleDisconnect,
	loading,
	record,
	disconnectLoading,
}: {
	subdomain: string;
	setSubdomain: React.Dispatch<React.SetStateAction<string>>;
	domainStatus: DomainStatus | null;
	handleAddDomain: () => void;
	handleCheckStatus: () => void;
	handleDisconnect: () => void;
	loading: boolean;
	record: any;
	disconnectLoading: boolean;
}) => {
	const [customDomainLink, setCustomDomainLink] = useState<boolean>(false);

	const handleCloseModal = () => {
		setCustomDomainLink(false);
	};

	return (
		<>
			<div className="w-full">
				{!domainStatus ? (
					<>
						<Card className="mb-6">
							<div className="flex items-center mb-4">
								<GlobalOutlined className="text-xl mr-2" />
								<Title level={4} className="!mb-0">
									Connect Subdomain
								</Title>
							</div>

							<Paragraph className="text-gray-600 mb-6">
								Add a custom subdomain to point to our application. Only subdomains are supported.
							</Paragraph>

							<div className="mb-4">
								<Text strong className="block mb-2">
									Subdomain
								</Text>
								<Input
									placeholder="app.yourdomain.com"
									value={subdomain}
									onChange={(e) => setSubdomain(e.target.value)}
									size="large"
									className="mb-2"
								/>
								<Text type="secondary" className="text-sm">
									Enter your subdomain (e.g., app.yourdomain.com, dashboard.mysite.com)
								</Text>
							</div>

							<Button
								type="primary"
								size="large"
								block
								onClick={handleAddDomain}
								loading={loading}
								disabled={!subdomain.trim()}
								className="bg-black hover:bg-gray-800 border-black"
							>
								Add Subdomain
							</Button>
						</Card>

						<Card>
							<Title level={4}>Quick Tips</Title>
							<div className="space-y-4 mt-5">
								<Alert
									message={<span className="font-semibold">Subdomain Only</span>}
									description="We only support subdomains like app.yourdomain.com"
									type="info"
									className="p-3"
								/>
								<Alert
									message={<span className="font-semibold">DNS Propagation</span>}
									description="Changes may take up to 24 hours to propagate"
									type="warning"
									className="p-3"
								/>
								<Alert
									message={<span className="font-semibold">SSL Certificate</span>}
									description="Automatic SSL provisioning after DNS verification"
									type="success"
									className="p-3"
								/>
							</div>
						</Card>
					</>
				) : (
					<div className="space-y-6">
						<Card title="DNS Configuration Required" className="border-orange-200">
							<Alert
								message="Update your DNS settings"
								description="Add the following CNAME record to your domain's DNS settings to complete the setup."
								type="warning"
								className="mb-5 p-3"
							/>
							<div className="bg-gray-900 text-white p-4 rounded-lg font-mono text-sm">
								<div>
									Type: <span className="text-white">CNAME</span>
									<CopyOutlined
										className="cursor-pointer text-gray-500 hover:text-gray-700 ml-2"
										onClick={() => {
											navigator.clipboard.writeText('CNAME');
											notification.success({ message: 'Content copied successfully' });
										}}
									/>
								</div>
								<div>
									Name: <span className="text-white">{domainStatus.dnsRecord.name}</span>{' '}
									<CopyOutlined
										className="cursor-pointer text-gray-500 hover:text-gray-700 ml-2"
										onClick={() => {
											if (domainStatus.dnsRecord.name) {
												navigator.clipboard.writeText(domainStatus.dnsRecord.name);
												notification.success({ message: 'Content copied successfully' });
											}
										}}
									/>
								</div>
								<div>
									Value: <span className="text-white">{domainStatus.dnsRecord.value}</span>{' '}
									<CopyOutlined
										className="cursor-pointer text-gray-500 hover:text-gray-700 ml-2"
										onClick={() => {
											if (domainStatus.dnsRecord.name) {
												navigator.clipboard.writeText(domainStatus.dnsRecord.value);
												notification.success({ message: 'Content copied successfully' });
											}
										}}
									/>
								</div>
							</div>

							{record?.map((r: any, index: number) => (
								<div
									key={index}
									className="bg-gray-900 text-white p-4 rounded-lg font-mono text-sm mb-4 mt-2"
								>
									<div>
										Type: <span className="text-white">{r.type}</span>{' '}
										<CopyOutlined
											className="cursor-pointer text-gray-500 hover:text-gray-700 ml-2"
											onClick={() => {
												if (r.type) {
													navigator.clipboard.writeText(r.type);
													notification.success({ message: 'Content copied successfully' });
												}
											}}
										/>
									</div>
									<div>
										Name: <span className="text-white">{r.domain}</span>
										<CopyOutlined
											className="cursor-pointer text-gray-500 hover:text-gray-700 ml-2"
											onClick={() => {
												if (r.domain) {
													navigator.clipboard.writeText(r.domain);
													notification.success({ message: 'Content copied successfully' });
												}
											}}
										/>
									</div>
									<div>
										Value: <span className="text-white">{r.value}</span>
										<CopyOutlined
											className="cursor-pointer text-gray-500 hover:text-gray-700 ml-2"
											onClick={() => {
												if (r.value) {
													navigator.clipboard.writeText(r.value);
													notification.success({ message: 'Content copied successfully' });
												}
											}}
										/>
									</div>
								</div>
							))}
						</Card>

						<Card>
							<div className="flex items-center justify-between mb-4">
								<div className="flex items-center">
									<GlobalOutlined className="text-xl mr-2" />
									<Title level={4} className="!mb-0">
										Domain Status
									</Title>
								</div>
								<Button
									danger
									icon={<DisconnectOutlined />}
									onClick={handleDisconnect}
									loading={disconnectLoading}
								>
									Disconnect Subdomain
								</Button>
							</div>

							<div className="bg-gray-50 p-4 rounded-lg mb-4">
								<Text strong className="text-lg">
									{domainStatus.domain}
								</Text>
								<div className="flex items-center gap-4 mt-2">
									<div className="flex items-center">
										<Text className="mr-2">Status:</Text>
										{domainStatus.status === 'verified' ? (
											<Tag color="green" icon={<CheckCircleOutlined />}>
												Verified
											</Tag>
										) : domainStatus.status === 'failed' ? (
											<Tag color="red" icon={<ExclamationCircleOutlined />}>
												Failed
											</Tag>
										) : (
											<Tag color="orange" icon={<ClockCircleOutlined />}>
												Pending
											</Tag>
										)}
									</div>
									<div className="flex items-center">
										<Text className="mr-2">SSL:</Text>
										{domainStatus.sslStatus === 'active' ? (
											<Tag color="green" icon={<CheckCircleOutlined />}>
												Active
											</Tag>
										) : (
											<Tag color="orange" icon={<ClockCircleOutlined />}>
												Pending
											</Tag>
										)}
									</div>
								</div>
								<p className="mt-3">
									<strong>Note:</strong> Once the status shows as "Verified" and SSL is marked as
									"Active," your custom domain is ready to use. However, it's recommended to wait an
									additional 15–30 minutes after activation before starting to use it to ensure full
									propagation.
								</p>
							</div>

							<Button
								type="primary"
								icon={<ReloadOutlined />}
								onClick={handleCheckStatus}
								loading={loading}
								className="mb-4 bg-black hover:bg-gray-800 border-black"
							>
								Check Status
							</Button>

							<Button
								type="default"
								icon={<LinkOutlined />}
								loading={loading}
								className="mb-4 ml-2"
								onClick={() => setCustomDomainLink(true)}
							>
								Custom Domain Usage
							</Button>
						</Card>
					</div>
				)}
			</div>
			<Modal footer={null} onCancel={handleCloseModal} open={customDomainLink}>
				<h3 className="mb-4 font-semibold text-xl">Custom Domain Integration</h3>
				<p className="text-sm mb-4">
					Allow your customers to access their dashboard directly using custom domain.
				</p>
				<div className="mb-6">
					<p className="text-sm font-medium mb-2">Login</p>
					<Input
						value={`https://${subdomain}/login`}
						readOnly
						suffix={
							<CopyOutlined
								className="cursor-pointer text-gray-500 hover:text-gray-700"
								onClick={() => {
									navigator.clipboard.writeText(`https://${subdomain}/login`);
									notification.success({ message: 'URL copied successfully' });
								}}
							/>
						}
					/>
				</div>
				<Divider />

				<div className="mb-6">
					<p className="text-sm font-medium mb-2">
						Paid Registration{' '}
						<Tooltip title="Your clients will create an account without a trial. They will automatically take one of your paid seats upon creation.">
							<InfoCircleOutlined />
						</Tooltip>
					</p>
					<Input
						value={`https://${subdomain}/register-receptionist`}
						readOnly
						suffix={
							<CopyOutlined
								className="cursor-pointer text-gray-500 hover:text-gray-700"
								onClick={() => {
									navigator.clipboard.writeText(`https://${subdomain}/register-receptionist`);
									notification.success({ message: 'URL copied successfully' });
								}}
							/>
						}
					/>
				</div>

				<div className="mb-6">
					<p className="text-sm font-medium mb-2">
						Trial Registration{' '}
						<Tooltip title="This will give your clients an account with 15 test free calls. They will go into trial accounts and will be able to convert from there.">
							<InfoCircleOutlined />
						</Tooltip>
					</p>
					<Input
						value={`https://${subdomain}/client-trial`}
						readOnly
						suffix={
							<CopyOutlined
								className="cursor-pointer text-gray-500 hover:text-gray-700"
								onClick={() => {
									navigator.clipboard.writeText(`https://${subdomain}/client-trial`);
									notification.success({ message: 'URL copied successfully' });
								}}
							/>
						}
					/>
				</div>
			</Modal>
		</>
	);
};

const SetupGuideTab = () => (
	<div className="w-full space-y-8">
		{/* Setup Guide */}
		<Card>
			<div className="flex items-center mb-4">
				<InfoCircleOutlined className="text-xl mr-2" />
				<Title level={4} className="!mb-0">
					Setup Guide
				</Title>
			</div>
			<Paragraph className="text-gray-600">
				Follow these steps to connect your subdomain to your Vercel application
			</Paragraph>

			<div className="space-y-6">
				{/* Step 1 */}
				<div className="border-l-4 border-blue-500 pl-4 mb-6">
					<div className="flex items-center mb-2">
						<Tag color="#108ee9">Step 1</Tag>
						<Title level={5} className="!mb-0">
							{' '}
							Add Your Subdomain
						</Title>
					</div>

					<Paragraph>
						Enter your subdomain in the "Connect Domain" tab. Make sure it follows the format:
						subdomain.yourdomain.com
					</Paragraph>
					<Alert
						message="Important: We only support subdomains. Root domains (yourdomain.com) are not allowed."
						type="info"
						showIcon
						className="mt-2"
					/>
				</div>

				{/* Step 2 */}

				<div className="border-l-4 border-orange-500 pl-4 mb-6">
					<div className="flex items-center mb-2">
						<Tag color="#f50">Step 2</Tag>
						<Title level={5} className="!mb-0">
							{' '}
							Configure DNS Settings
						</Title>
					</div>

					<Paragraph>Add a CNAME record to your domain's DNS settings:</Paragraph>

					<div className="bg-gray-900 text-white p-4 rounded-lg font-mono text-sm mt-3">
						<div className="text-blue-300">
							Type: <span className="text-white">CNAME</span>
						</div>
						<div className="text-green-300">
							Name: <span className="text-white">[your-subdomain]</span>
						</div>
						<div className="text-yellow-300">
							Value: <span className="text-white">cname.vercel-dns.com</span>
						</div>
					</div>

					<div className="mt-3 p-3 bg-gray-50 rounded">
						<Text strong>Example: </Text>For subdomain "app.mycompany.com":
						<ul className="mt-2 ml-4 text-sm">
							<li>Type: CNAME</li>
							<li>Name: app</li>
							<li>Value: cname.vercel-dns.com</li>
						</ul>
					</div>
				</div>

				{/* Step 3 */}

				<div className="border-l-4 border-green-500 pl-4 mb-6">
					<div className="flex items-center mb-2">
						<Tag color="#87d068">Step 3</Tag>
						<Title level={5} className="!mb-0">
							{' '}
							Wait for Verification
						</Title>
					</div>
					<Paragraph>
						Our system will automatically verify your DNS configuration. This process may take up to
						24 hours.
					</Paragraph>
					<div className="flex items-center gap-2 p-3 bg-green-50 rounded mt-2">
						<CheckCircleOutlined className="text-green-600" />
						<Text>
							Once verified, an SSL certificate will be automatically provisioned for your
							subdomain.
						</Text>
					</div>
				</div>
			</div>
		</Card>

		{/* Popular DNS Providers */}
		<Card className="mt-8">
			<Title level={3}>Popular DNS Providers</Title>
			<Paragraph className="text-gray-600 mb-4">
				Quick links to DNS management for common providers
			</Paragraph>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{[
					{ name: 'Cloudflare', links: 'https://www.cloudflare.com/' },
					{ name: 'GoDaddy', links: 'https://www.godaddy.com/en-in' },
					{ name: 'Namecheap', links: 'https://www.namecheap.com/' },
					{ name: 'Google Domains', links: 'https://domains.google/' },
					{ name: 'Route 53', links: 'https://aws.amazon.com/route53/' },
					{ name: 'Hostinger', links: 'https://www.hostinger.in/' },
				].map((provider) => (
					<Button
						key={provider.name}
						type="default"
						className="h-12 flex items-center justify-between text-left"
						// icon={<span className="text-lg">{provider.icon}</span>}
					>
						<span className="font-semibold">{provider.name}</span>
						<a href={provider.links} target="_blank" rel="noopener noreferrer">
							<LinkOutlined className="text-gray-400" />
						</a>
					</Button>
				))}
			</div>
		</Card>

		{/* Troubleshooting */}
		<Card>
			<div className="flex items-center mb-4">
				<InfoCircleOutlined className="text-xl mr-2" />
				<Title level={4} className="!mb-0">
					Troubleshooting
				</Title>
			</div>

			<div className="space-y-4">
				<Title level={5}>Common Issues</Title>

				<div className="space-y-4">
					<Card size="small" className="border-red-200">
						<Title level={5} className="text-red-600 mb-2">
							Domain verification failed
						</Title>
						<Text>
							Check that your CNAME record is correctly configured and allow up to 24 hours for DNS
							propagation.
						</Text>
					</Card>

					<Card size="small" className="border-orange-200">
						<Title level={5} className="text-orange-600 mb-2">
							SSL certificate pending
						</Title>
						<Text>
							SSL certificates are issued automatically after DNS verification. This process may
							take several minutes.
						</Text>
					</Card>

					<Card size="small" className="border-yellow-200">
						<Title level={5} className="text-yellow-600 mb-2">
							Subdomain not accessible
						</Title>
						<Text>
							Ensure your subdomain status shows as "Active" and try clearing your browser cache.
						</Text>
					</Card>
				</div>
			</div>
		</Card>
	</div>
);

const CustomDomainForm: React.FC = () => {
	const [subdomain, setSubdomain] = useState('');
	const [domainStatus, setDomainStatus] = useState<DomainStatus | null>(null);
	const [loading, setLoading] = useState(false);
	const [record, setRecord] = useState<any>(null);
	const [disconnectLoading, setDisconnectLoading] = useState(false);

	const { refetch } = useQuery({
		queryKey: ['reseller'],
		queryFn: () => getReseller({ from: USER_TYPES.RESELLER }),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const { mutate: addDomain } = useMutation({
		mutationFn: (subdomain: string) => updateDomain({ domain: subdomain }),
	});

	// Disconnect domain
	const { mutate: disconnectDomain } = useMutation({
		mutationFn: (subdomain: string) => deleteDomain({ domain: subdomain }),
	});

	// Check Status
	const { mutate: checkStatus } = useMutation({
		mutationFn: (subdomain: string) => dnsStatus({ domain: subdomain }),
	});

	const handleAddDomain = () => {
		if (!subdomain.trim()) {
			return;
		}
		setLoading(true);
		addDomain(subdomain, {
			onSuccess: (data) => {
				setLoading(false);
				setDomainStatus({
					domain: subdomain,
					status: 'pending',
					sslStatus: 'pending',
					dnsRecord: {
						type: 'CNAME',
						name: subdomain.split('.')[0],
						value: 'cname.vercel-dns.com',
					},
				});
				setRecord(data.vercelResponse.vercel.data.verification);
				console.log(data);
			},
			onError: (error) => {
				console.log(error);
			},
		});
	};

	const handleCheckStatus = () => {
		checkStatus(subdomain, {
			onSuccess: (data) => {
				console.log(data);
			},
			onError: (error) => {
				console.log(error);
			},
		});
	};

	const handleDisconnect = () => {
		setDisconnectLoading(true);
		disconnectDomain(subdomain, {
			onSuccess: () => {
				setDomainStatus(null);
				setSubdomain('');
				setDisconnectLoading(false);
			},
			onError: (error) => {
				console.log(error);
			},
		});
	};

	const resellerData = useAppSelector(getResellerData);

	useEffect(() => {
		refetch();
		if (resellerData?.customDomain) {
			setLoading(true);
			setSubdomain(resellerData.customDomain);
			setDomainStatus({
				domain: resellerData.customDomain,
				status: 'verified',
				sslStatus: 'active',
				dnsRecord: {
					type: 'CNAME',
					name: resellerData.customDomain.split('.')[0],
					value: 'cname.vercel-dns.com',
				},
			});
			setLoading(false);
		}
	}, [resellerData?.customDomain]);

	return (
		<Tabs
			defaultActiveKey="connect"
			size="large"
			items={[
				{
					key: 'connect',
					label: (
						<span>
							<GlobalOutlined /> Connect Subdomain
						</span>
					),
					children: (
						<ConnectSubdomainTab
							subdomain={subdomain}
							setSubdomain={setSubdomain}
							domainStatus={domainStatus}
							handleAddDomain={handleAddDomain}
							handleCheckStatus={handleCheckStatus}
							handleDisconnect={handleDisconnect}
							loading={loading}
							record={record}
							disconnectLoading={disconnectLoading}
						/>
					),
				},
				{
					key: 'guide',
					label: (
						<span>
							<InfoCircleOutlined /> Setup Guide
						</span>
					),
					children: <SetupGuideTab />,
				},
			]}
		/>
	);
};

export default CustomDomainForm;
